<template>
  <div class="bg-white w-full scroll-styling overflow-y-scroll">
    <div class="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Мои отклики</h1>
        <p class="text-gray-600">Здесь вы можете видеть все ваши отклики и общаться с авторами объявлений</p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-16">
        <p class="text-gray-400 text-lg">Загрузка откликов...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="responses.length === 0" class="text-center py-16">
        <p class="text-gray-500 text-lg">У вас пока нет откликов</p>
        <router-link
          :to="{ name: 'ExpertSearch' }"
          class="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          Перейти к объявлениям
        </router-link>
      </div>

      <!-- Responses List -->
      <div v-else class="space-y-4">
        <div
          v-for="response in responses"
          :key="response.id"
          class="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition overflow-hidden"
        >
          <!-- Response Header -->
          <div
            @click="toggleResponse(response.id)"
            class="p-4 md:p-6 cursor-pointer hover:bg-gray-50 transition border-b"
          >
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-800">{{ response.announcement_title }}</h3>
                <p class="text-sm text-gray-600 mt-1">От: {{ response.applicant_name }}</p>
              </div>
              <div class="flex flex-col items-end gap-2">
                <span :class="getStatusBadgeClass(response.status)">
                  {{ getStatusLabel(response.status) }}
                </span>
                <span class="text-xs text-gray-500">{{ formatDate(response.created_at) }}</span>
              </div>
            </div>
          </div>

          <!-- Messages Section -->
          <div
            v-if="selectedResponse === response.id"
            class="border-t bg-gray-50"
          >
            <!-- Loading Messages -->
            <div v-if="loadingDetail" class="p-4 md:p-6 text-center">
              <p class="text-gray-400">Загрузка чата...</p>
            </div>

            <!-- Messages List -->
            <div v-else class="p-4 md:p-6 space-y-3 max-h-96 overflow-y-auto">
              <div v-if="!responseDetail?.messages || responseDetail.messages.length === 0" class="text-center py-8">
                <p class="text-gray-500">Нет сообщений</p>
              </div>

              <div
                v-for="message in responseDetail?.messages || []"
                :key="message.id"
                :class="[
                  'p-3 rounded-lg',
                  message.is_deleted ? 'bg-gray-200 text-gray-500 italic' : 'bg-white border border-gray-200'
                ]"
              >
                <div class="flex justify-between items-start gap-2 mb-2">
                  <span class="text-sm font-semibold text-gray-800">{{ message.sender_name }}</span>
                  <span class="text-xs text-gray-500">{{ formatDate(message.created_at) }} {{ formatTime(message.created_at) }}</span>
                </div>
                <p class="text-sm text-gray-700">
                  {{ message.is_deleted ? '[Сообщение удалено]' : message.message }}
                </p>
              </div>
            </div>

            <!-- Status Actions (for announcement author) -->
            <div v-if="isAnnouncementAuthor" class="p-4 md:p-6 border-t bg-blue-50 space-y-3">
              <!-- Show message if status is final -->
              <div v-if="responseDetail?.status === 'accepted' || responseDetail?.status === 'rejected'" class="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p class="text-sm text-yellow-800">
                  <span v-if="responseDetail?.status === 'accepted'">Отклик принят. Статус не может быть изменён.</span>
                  <span v-if="responseDetail?.status === 'rejected'">Отклик отклонён. Статус не может быть изменён.</span>
                </p>
              </div>

              <!-- Show buttons only if status is not final -->
              <div v-if="responseDetail?.status !== 'accepted' && responseDetail?.status !== 'rejected'" class="space-y-3">
                <p class="text-sm font-medium text-gray-700">Действия с откликом:</p>
                <div class="flex gap-2 flex-wrap">
                  <button
                    @click="updateResponseStatus(response.id, 'accepted')"
                    :disabled="updatingStatus"
                    class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ updatingStatus ? '⏳ Обновление...' : 'Принять' }}
                  </button>
                  <button
                    @click="updateResponseStatus(response.id, 'rejected')"
                    :disabled="updatingStatus"
                    class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ updatingStatus ? '⏳ Обновление...' : 'Отклонить' }}
                  </button>
                  <button
                    @click="updateResponseStatus(response.id, 'read')"
                    :disabled="updatingStatus || responseDetail?.status === 'read'"
                    class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ updatingStatus ? '⏳ Обновление...' : 'Отметить прочитанным' }}
                  </button>
                </div>
              </div>

              <div v-if="statusError" class="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                <p class="text-sm text-red-700">{{ statusError }}</p>
              </div>
            </div>

            <!-- Message Input -->
            <div class="p-4 md:p-6 border-t bg-white">
              <div class="space-y-3">
                <textarea
                  v-model="newMessage"
                  placeholder="Напишите ответное сообщение..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
                  rows="3"
                ></textarea>

                <div v-if="messageError" class="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p class="text-sm text-red-700">{{ messageError }}</p>
                </div>

                <button
                  @click="sendMessage(response.id)"
                  :disabled="messageSending || !newMessage.trim()"
                  class="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ messageSending ? 'Отправка...' : 'Отправить сообщение' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useStore } from 'vuex';
import { API_ANNOUNCEMENT_RESPONSES, API_RESPONSE_MESSAGES, API_PILOT_ANNOUNCEMENTS_PUBLIC, HEADERS_TOKEN_JSON } from '@/const/uri';

export default {
  name: 'ResponsesList',
  setup() {
    const store = useStore();
    const responses = ref([]);
    const responseDetail = ref(null);
    const loading = ref(false);
    const loadingDetail = ref(false);
    const selectedResponse = ref(null);
    const newMessage = ref('');
    const messageError = ref('');
    const messageSending = ref(false);
    const updatingStatus = ref(false);
    const statusError = ref('');

    const fetchResponses = async () => {
      loading.value = true;
      try {
        const response = await axios.get(
          `${API_ANNOUNCEMENT_RESPONSES.list}`,
          { headers: HEADERS_TOKEN_JSON() }
        );

        if (response.data && response.data.data && response.data.data.data) {
          responses.value = response.data.data.data.results || [];
        }
      } catch (error) {
        console.error('Error fetching responses:', error);
      } finally {
        loading.value = false;
      }
    };

    const toggleResponse = async (responseId) => {
      if (selectedResponse.value === responseId) {
        selectedResponse.value = null;
        responseDetail.value = null;
      } else {
        selectedResponse.value = responseId;
        await fetchResponseDetail(responseId);
      }
    };

    const fetchResponseDetail = async (responseId) => {
      loadingDetail.value = true;
      try {
        const response = await axios.get(
          `${API_ANNOUNCEMENT_RESPONSES.base}${responseId}/`,
          { headers: HEADERS_TOKEN_JSON() }
        );

        if (response.data?.data?.data) {
          const responseData = response.data.data.data;

          // Also fetch announcement details to get author info
          if (responseData.announcement_id) {
            try {
              const announcementRes = await axios.get(
                `${API_PILOT_ANNOUNCEMENTS_PUBLIC.base}${responseData.announcement_id}/`,
                { headers: HEADERS_TOKEN_JSON() }
              );

              if (announcementRes.data?.data?.data) {
                // Attach announcement object to response
                responseData.announcement = announcementRes.data.data.data;
                console.log('Loaded announcement:', responseData.announcement);
                console.log('Announcement author_name:', responseData.announcement.author_name);
              }
            } catch (announcementError) {
              console.error('Error fetching announcement details:', announcementError);
            }
          }

          responseDetail.value = responseData;
        }
      } catch (error) {
        console.error('Error fetching response detail:', error);
        responseDetail.value = null;
      } finally {
        loadingDetail.value = false;
      }
    };

    const sendMessage = async (responseId) => {
      if (!newMessage.value.trim()) {
        messageError.value = 'Пожалуйста, напишите сообщение';
        return;
      }

      messageSending.value = true;
      messageError.value = '';

      try {
        await axios.post(
          `${API_RESPONSE_MESSAGES.create}`,
          {
            response: responseId,
            message: newMessage.value,
          },
          { headers: HEADERS_TOKEN_JSON() }
        );

        newMessage.value = '';
        // Refresh the response detail to show the new message
        await fetchResponseDetail(responseId);
      } catch (error) {
        console.error('Error sending message:', error);
        messageError.value = 'Ошибка при отправке сообщения. Попробуйте позже.';
      } finally {
        messageSending.value = false;
      }
    };

    const updateResponseStatus = async (responseId, newStatus) => {
      updatingStatus.value = true;
      statusError.value = '';

      try {
        await axios.patch(
          `${API_ANNOUNCEMENT_RESPONSES.base}${responseId}/`,
          {
            status: newStatus,
          },
          { headers: HEADERS_TOKEN_JSON() }
        );

        // Refresh the response detail to show the updated status
        await fetchResponseDetail(responseId);
      } catch (error) {
        console.error('Error updating response status:', error);
        statusError.value = 'Ошибка при обновлении статуса. Попробуйте позже.';
      } finally {
        updatingStatus.value = false;
      }
    };

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('ru-RU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    };

    const formatTime = (dateStr) => {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    };

    const getStatusLabel = (status) => {
      const labels = {
        'new': 'Новый',
        'read': 'Прочитан',
        'accepted': 'Принят',
        'rejected': 'Отклонён',
      };
      return labels[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        'new': 'inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold',
        'read': 'inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold',
        'accepted': 'inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold',
        'rejected': 'inline-block px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold',
      };
      return classes[status] || 'inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold';
    };

    const isAnnouncementAuthor = computed(() => {
      // Check if current user is the announcement author (can manage response status)
      if (!responseDetail.value) {
        return false;
      }

      const currentUser = store.state.user;
      if (!currentUser || !currentUser.fio) {
        return false;
      }

      const announcement = responseDetail.value.announcement;
      if (!announcement) {
        return false;
      }

      console.log('Current user fio:', currentUser.fio);
      console.log('Announcement author_name:', announcement.author_name);
      console.log('Are they equal?', currentUser.fio === announcement.author_name);

      // Compare user's fio with announcement author_name
      // Both come from the User model
      return currentUser.fio === announcement.author_name;
    });

    onMounted(() => {
      fetchResponses();
    });

    return {
      responses,
      responseDetail,
      loading,
      loadingDetail,
      selectedResponse,
      newMessage,
      messageError,
      messageSending,
      updatingStatus,
      statusError,
      isAnnouncementAuthor,
      toggleResponse,
      sendMessage,
      updateResponseStatus,
      formatDate,
      formatTime,
      getStatusLabel,
      getStatusBadgeClass,
    };
  },
};
</script>

<style scoped>
textarea {
  font-family: inherit;
}

textarea:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

button:disabled {
  opacity: 0.6;
}
</style>
